import { useCallback, useState } from "react";

/**
 * Shared drag-and-drop interaction for every file-picking surface in the
 * app (raise-ask documents, evidence photos, the reporter's quick-capture
 * tray, a site-visit checklist photo, a bank statement CSV). Before this,
 * exactly one screen (StatementUpload.tsx) had hand-rolled drag support;
 * everywhere else was click-to-pick only via a hidden `<input type="file">`.
 *
 * Deliberately just a hook, not a wrapping component: every call site
 * already has its own bespoke drop-zone markup (a dashed-border box, a
 * button, a thumbnail grid) that varies too much to force through one
 * generic visual component without either flattening real differences
 * (single vs multi-file, accept types, mobile camera capture) or building
 * something more complex than the problem needs. This hook supplies the
 * event handlers and the "something's hovering over me" boolean; the
 * caller spreads the handlers onto whatever element it already renders as
 * the drop target and drives its own highlight styling off `isDraggingOver`.
 *
 * Callers should route dropped files through the SAME handling function
 * their file `<input>`'s onChange already calls (compression, previews,
 * whatever) - onFiles is the one place that logic should live, not
 * duplicated between the input and the drop handler.
 */
export interface UseFileDropOptions {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export interface FileDropHandlers {
  /** True while a drag carrying files is currently over the drop target - drive a highlight style off this. */
  isDraggingOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export function useFileDrop({ onFiles, disabled }: UseFileDropOptions): FileDropHandlers {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDraggingOver(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0) onFiles(files);
    },
    [disabled, onFiles],
  );

  return { isDraggingOver, onDragOver, onDragLeave, onDrop };
}
